import { create, DescMessage, toBinary } from '@bufbuild/protobuf'
import { base64Encode } from '@bufbuild/protobuf/wire'

import { FlowType } from '@cash-web/protos/squareup/cash/plasma/flows/FlowProto.pb'
import { StatusResult } from '@cash-web/protos/squareup/franklin/status_result.pb'
import { BlockerType, FlowBlockerDescriptor, FlowData, getBlockerType } from '@cash-web/shared-flow-data-access-context'

import { FlowReducerAction, FlowState } from './hooks/flowReducer'
import { FlowResponse } from './middleware/utils'

export const DEFAULT_ERROR_MESSAGE = 'Oops, something went wrong. Please refresh the page and try again.'
export const getActiveBlocker = (skippedBlockers: BlockerType[], blockerDescriptors: FlowBlockerDescriptor[]) => {
  return blockerDescriptors.find(blockerDescriptor => {
    return !skippedBlockers.includes(getBlockerType(blockerDescriptor.blocker))
  })
}

export const getBlockerDescriptorsFromAction = (action: FlowReducerAction) => {
  if ('blockerDescriptors' in action) {
    return action.blockerDescriptors?.filter(isNotEmptyBlocker)
  }
  return undefined
}

const isNotEmptyFormBlocker = (blockerDescriptor: FlowBlockerDescriptor) => {
  const formBlocker = blockerDescriptor.blocker?.form
  return formBlocker == null || (formBlocker.elements != null && formBlocker.elements.length !== 0)
}

export const isNotEmptyBlocker = (blockerDescriptor: FlowBlockerDescriptor) => {
  return Object.keys(blockerDescriptor).length !== 0 && isNotEmptyFormBlocker(blockerDescriptor)
}

export const isFlowComplete = (data: FlowData | FlowState | FlowResponse) => {
  let blockerDescriptors: FlowBlockerDescriptor[] | undefined = []
  let statusResult: StatusResult | undefined

  if ('blockerDescriptors' in data) {
    blockerDescriptors = data.blockerDescriptors?.filter(isNotEmptyBlocker)
  }
  if ('responseContext' in data) {
    statusResult = data.responseContext?.status_result
  } else if ('response_context' in data) {
    blockerDescriptors = data.response_context?.scenario_plan?.blocker_descriptors?.filter(isNotEmptyBlocker)
    statusResult = data.response_context?.status_result
  }

  const hasNoBlockers = blockerDescriptors == null || blockerDescriptors.length === 0
  const hasNoStatusResult = statusResult == null

  return hasNoBlockers && hasNoStatusResult
}

export function createInitiationData({
  flowType,
  params,
  encodeFormat = 'json',
  protobufSchema,
}: {
  flowType: FlowType
  params?: Record<string, unknown>
  encodeFormat?: 'json' | 'binary'
  protobufSchema?: DescMessage
}): string {
  if (!params || Object.keys(params).length === 0) {
    return flowType
  }

  if (encodeFormat === 'binary') {
    if (!protobufSchema) {
      throw new Error('protobufSchema is required when encodeFormat is binary')
    }

    const protobufMessage = create(protobufSchema, params)
    const binaryParams = toBinary(protobufSchema, protobufMessage)
    return `${flowType}?params=${base64Encode(binaryParams)}`
  }

  const encodedParams = btoa(JSON.stringify(params))
  return `${flowType}?params_type=json&params=${encodedParams}`
}

const flowTypeAmended = {
  ...FlowType,
  PIGGYBANK_SAVING_YIELD_ONBOARDING_FLOW: 'PIGGYBANK_SAVING_YIELD_ONBOARDING_FLOW',
} as const
export function isFlowType(flowTypeString: string): flowTypeString is FlowType {
  return Object.keys(flowTypeAmended).includes(flowTypeString)
}

export function parseFlowTypeFromInitiationData(initiationData?: string) {
  return initiationData?.split(/\?|%3F/i, 1)[0] || undefined
}

export function getFlowType(data: Partial<FlowData>): FlowType | undefined {
  if ('flow' in data) {
    const flowType = data.flow?.flowType ?? parseFlowTypeFromInitiationData(data.flow?.initiationData)
    return flowType && isFlowType(flowType) ? flowType : undefined
  }
  return undefined
}

/**
 * Determines whether a blocker should submit immediately or accumulate inputs locally.
 * Used for multi-blocker groups where blockers with the same group_id should batch their submissions.
 */
export function shouldSubmitImmediately(
  currentBlocker: FlowBlockerDescriptor | undefined,
  nextBlocker: FlowBlockerDescriptor | undefined
): boolean {
  const currentGroupId = currentBlocker?.group_id

  // No current blocker or no group - submit immediately (existing behavior)
  // Note: Both undefined and empty string ('') are treated as "no group" since
  // falsy group_id values indicate the blocker is not part of a multi-blocker group
  if (!currentBlocker || !currentGroupId) return true

  // No next blocker - must submit
  if (!nextBlocker) return true

  // Next blocker has different/no group - submit current group
  if (nextBlocker.group_id !== currentGroupId) return true

  // Same group - accumulate locally
  return false
}
